import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useDirtyDialogGuard } from '@/features/patient-record/hooks/useDirtyDialogGuard';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { useUploadDocument } from '@/features/patient-record/hooks/useDocumentMutations';
import { useUpdateTestOrder } from '@/features/patient-record/hooks/useTestOrderMutations';
import { patientQueries } from '@/features/patient-record/queries';
import type { TestOrderViewModel } from '@/features/patient-record/types';

import { FormField } from '../_shared/FormField';

import { toTestOrderInput } from './toTestOrderInput';

interface RecordResultDialogProps {
  readonly guid: string;
  readonly order: TestOrderViewModel;
  readonly onClose: () => void;
}

interface ResultDraft {
  readonly resultDate: string;
  readonly resultSummary: string;
  readonly reportTitle: string;
  readonly file: File | null;
}

/**
 * PTV2-1563 — the "Resulted" step, with the lab report attached to it.
 *
 * Moving an order to RESULTED from the row's status dropdown used to PUT a
 * bare status change, which the BE rejects: RESULTED needs a result summary
 * or an external report URL. This dialog collects that evidence instead, and
 * takes the report file in the same pass. The file is uploaded to the
 * patient's attachments, so it lands in the Documents section as well.
 */
export function RecordResultDialog({
  guid,
  order,
  onClose,
}: RecordResultDialogProps): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const updateMutation = useUpdateTestOrder(guid);
  const uploadMutation = useUploadDocument(guid);
  const displayName = order.panelName ?? order.testName;
  const alreadyResulted = order.status === 'RESULTED';

  const [initial] = useState<ResultDraft>(() => ({
    resultDate: order.resultDate ?? new Date().toISOString().slice(0, 10),
    resultSummary: order.resultSummary ?? '',
    reportTitle: t('patient.testOrders.result.reportTitleDefault', {
      value: displayName,
    }),
    file: null,
  }));

  const pending = updateMutation.isPending || uploadMutation.isPending;

  // The BE needs a summary or an external report URL before it accepts
  // RESULTED; an order that already carries a URL satisfies that on its own.
  const hasExternalReport = (order.externalReportUrl ?? '').trim().length > 0;

  const commit = async (draft: ResultDraft): Promise<unknown> => {
    if (draft.file !== null) {
      // The upload prepends to the documents cache. Load the existing list
      // first so a patient whose Documents section was never opened doesn't
      // end up with a cache holding only this one row.
      await queryClient
        .query({ ...patientQueries.documents(guid), staleTime: 'static' })
        .catch(() => undefined);
      await uploadMutation.mutateAsync({
        title: draft.reportTitle.trim(),
        documentType: 'MEDICAL_REPORT',
        file: draft.file,
      });
    }
    const summary = draft.resultSummary.trim();
    return updateMutation.mutateAsync({
      id: order.id,
      input: toTestOrderInput(order, {
        status: 'RESULTED',
        resultDate: draft.resultDate.length > 0 ? draft.resultDate : null,
        resultSummary: summary.length > 0 ? summary : null,
      }),
    });
  };

  const form = useEditableDraft<ResultDraft>({
    initial,
    label: t('patient.testOrders.result.dialogTitle'),
    autosave: false,
    canSave: (d) => d.resultSummary.trim().length > 0 || hasExternalReport,
    isEqual: (a, b) =>
      a.resultDate === b.resultDate &&
      a.resultSummary === b.resultSummary &&
      a.reportTitle === b.reportTitle &&
      a.file === b.file,
    onSave: (d) => commit(d),
  });
  const { draft } = form;

  const submitAndClose = (): void => {
    if (!form.canSave || pending) return;
    void commit(draft).then(onClose).catch(() => undefined);
  };

  const guard = useDirtyDialogGuard({
    dirty: form.isDirty,
    canSave: form.canSave,
    saving: pending,
    label: t('patient.testOrders.result.dialogTitle'),
    onSave: submitAndClose,
    onClose,
    register: false,
  });

  const pickFile = (next: File | null): void => {
    form.setDraft((d) => ({
      ...d,
      file: next,
      // A report on its own is enough for the user, but not for the BE — seed
      // the summary they can then edit rather than blocking Save.
      resultSummary:
        next !== null && d.resultSummary.trim().length === 0
          ? t('patient.testOrders.result.attachedSummary')
          : d.resultSummary,
    }));
  };

  return (
    <>
      <Dialog open {...guard.rootProps}>
        <DialogContent className="sm:max-w-lg" {...guard.contentProps}>
          <DialogHeader>
            <DialogTitle>
              {alreadyResulted
                ? t('patient.testOrders.result.updateTitle')
                : t('patient.testOrders.result.dialogTitle')}
            </DialogTitle>
            <DialogDescription>
              {t(
                alreadyResulted
                  ? 'patient.testOrders.result.updateDescription'
                  : 'patient.testOrders.result.dialogDescription',
                { value: displayName },
              )}
            </DialogDescription>
          </DialogHeader>
          <form
            ref={form.containerProps.ref}
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              submitAndClose();
            }}
          >
            <FormField label={t('patient.testOrders.resultDate')} className="sm:w-1/2">
              <DateInput
                value={draft.resultDate}
                onChange={(value) => {
                  form.setDraft((d) => ({ ...d, resultDate: value }));
                }}
              />
            </FormField>

            <FormField
              label={t('patient.testOrders.resultSummary')}
              required={!hasExternalReport}
            >
              <Textarea
                value={draft.resultSummary}
                rows={3}
                placeholder={t('patient.testOrders.result.summaryPlaceholder')}
                onChange={(event) => {
                  form.setDraft((d) => ({ ...d, resultSummary: event.target.value }));
                }}
              />
            </FormField>

            <FormField label={t('patient.testOrders.result.fileLabel')}>
              <input
                type="file"
                onChange={(event) => {
                  pickFile(event.target.files?.[0] ?? null);
                }}
                className="text-xs file:mr-3 file:rounded file:border file:border-input file:bg-background file:px-2 file:py-1 file:text-xs"
              />
            </FormField>

            {draft.file !== null && (
              <FormField label={t('patient.testOrders.result.reportTitleLabel')}>
                <Input
                  type="text"
                  value={draft.reportTitle}
                  onChange={(event) => {
                    form.setDraft((d) => ({ ...d, reportTitle: event.target.value }));
                  }}
                  className="h-9 text-sm"
                />
              </FormField>
            )}

            <p className="text-xs text-muted-foreground">
              {t('patient.testOrders.result.documentsHint')}
            </p>

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="ghost"
                onClick={guard.requestClose}
                disabled={pending}
              >
                {t('ui.dialog.close')}
              </Button>
              <Button
                type="submit"
                disabled={!form.canSave || pending}
                loading={pending}
                loadingText={t('patient.dirtyFooter.saving')}
              >
                {t('patient.testOrders.result.submit')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {guard.confirm}
    </>
  );
}
